import React from "react";
import RadarChart from "./RadarChart";
import update from "immutability-helper";
import gsap from "gsap";
import { Draggable } from "gsap/all";
import ProjectAside from "./ProjectAside";
import { isMobile } from "./util";

import "./App.css";

const VERTICAL_MARGIN = 32 * 2;

const getSize = () =>
  isMobile()
    ? Math.min(window.innerWidth, window.innerHeight) - VERTICAL_MARGIN / 2
    : Math.min(window.innerWidth, window.innerHeight) - 200 - VERTICAL_MARGIN;

class App extends React.Component {
  state = {
    serverData: this.props.serverData,
    // prevIndex: "",
    active: "",
    title: "",
    description: "",
    slug: "",
    windowWidth: 0,
    backgroundColor: "transparent",
  };

  constructor(props) {
    super(props);

    const data = this.state.serverData[0];

    data.options.captionProps = this.captionProps;
    data.size = getSize();

    this.container = null; // ref
    this.draggable = null; // instance
  }

  componentDidMount() {
    if (isMobile()) this.bindDrag();
    this.bindResize();
  }

  componentWillUnmount() {
    this.unbindDrag();
    this.unbindResize();
  }

  bindDrag = () => {
    gsap.registerPlugin(Draggable);
    // gsap.registerPlugin(InertiaPlugin); // premium

    const { chart } = this.state.serverData[0];
    const len = chart.length;
    const increment = Math.round(360 / len);

    // https://greensock.com/docs/v3/Plugins/Draggable
    const [draggable] = Draggable.create(this.container, {
      type: "rotation",
      throwProps: true,
      dragClickables: true,
      // liveSnap: { // Allows you to define rules that get applied WHILE the element is being dragged
      //   rotation: function(value) {
      //     //snap to the closest increment of 10.
      //     return Math.round(value / 10) * 10;
      //   }
      // },
      // dragResistance: 0.5, // half-speed
      // inertia: true, // premium
      // snap: value => Math.round(value / 90) * 90 // premium
    });

    this.draggable = draggable;
    this.draggable.addEventListener("drag", e => {
      const { rotation } = this.draggable;

      let index = Math.floor(rotation / increment);
      index = Math.floor(index % len);

      if (Math.sign(index) === -1) {
        index += len;
      }

      const { title, description, slug, meta } = chart[index];
      const { color: backgroundColor } = meta;

      const state = update(this.state, {
        // prevIndex: { $set: index },
        active: { $set: slug },
        title: { $set: title },
        slug: { $set: slug },
        description: { $set: description },
        backgroundColor: { $set: backgroundColor },
        serverData: {
          0: {
            chart: prevEntries => {
              const nextEntries = [...prevEntries];
              const [entry] = nextEntries.splice(index, 1);

              entry.active = true;
              nextEntries.push(entry);

              return nextEntries;
            },
          },
        },
      });

      this.setState(state);
    });
  };

  unbindDrag = () => {
    this.draggable.kill();
  };

  handleResize = () => {
    const { innerWidth } = window;
    if (this.state.windowWidth === innerWidth) return;

    const state = update(this.state, {
      windowWidth: { $set: innerWidth },
      serverData: {
        0: {
          size: { $set: getSize() },
        },
      },
    });

    this.setState(state);
  };

  bindResize = () => {
    window.addEventListener("resize", this.handleResize);
    if (!this.draggable && isMobile()) this.bindDrag();
  };

  unbindResize = () => {
    window.removeEventListener("resize", this.handleResize);
  };

  captionProps = () => ({
    className: "caption",
    dominantBaseline: "central",
    fontSize: 16,
    fontFamily: "'Times New Roman', Times, serif",
    mouseEnter: this.captionMouseEnter,
    mouseLeave: this.captionMouseLeave,
    click: this.captionClick,
  });

  captionMouseEnter = ({ key, idx }) => {
    const { chart } = this.state.serverData[0];
    const index = chart.findIndex(entry => entry.slug === key);
    const { title, description, slug } = chart[index];

    const state = update(this.state, {
      // prevIndex: { $set: index },
      active: { $set: slug },
      title: { $set: title },
      slug: { $set: slug },
      description: { $set: description },
      serverData: {
        0: {
          chart: prevEntries => {
            // const nextEntries = [...prevEntries];
            const nextEntries = prevEntries.map(entry => {
              entry.active = false;
              return entry;
            });

            const [entry] = nextEntries.splice(index, 1);

            entry.active = true;
            nextEntries.push(entry);

            return nextEntries;
          },
        },
      },
    });

    this.setState(state);
  };

  captionMouseLeave = ({ key, idx }) => {
    // const { prevIndex } = this.state;
    // const state = update(this.state, {
    //   serverData: {
    //     0: {
    //       chart: prevEntries => {
    //         const nextEntries = prevEntries.map(entry => {
    //           entry.active = false;
    //           return entry;
    //         });
    //         const entry = nextEntries.pop();
    //         nextEntries.splice(prevIndex, 0, entry);
    //         return nextEntries;
    //       }
    //     }
    //   }
    // });
    // this.setState(state);
  };

  captionClick = ({ e, key: slug }) => {
    e.preventDefault();
    window.location.href = `/project/${slug}`;
  };

  render() {
    const {
      serverData,
      active,
      title,
      description,
      slug,
      backgroundColor,
    } = this.state;

    const { size } = serverData[0];
    const dotsContainerStyle = {
      top: VERTICAL_MARGIN,
      left: (window.innerWidth - size) / 2 - 32,
      width: size,
      height: size,
    };

    return (
      <React.Fragment>
        <div
          ref={node => (this.container = node)}
          className="dots-container"
          style={dotsContainerStyle}
        />
        <div className="radar-container">
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: "inherit",
              justifyContent: "center",
            }}
          >
            {serverData.map((data, index) => (
              <div className="chart-container" key={`${index}-${data.name}`}>
                <RadarChart
                  slug={slug}
                  captions={data.captions}
                  data={data.chart}
                  options={data.options}
                  size={data.size}
                  id={`chart-${index}`}
                />
              </div>
            ))}
          </div>
        </div>

        <ProjectAside
          active={active}
          title={title}
          description={description}
          slug={slug}
          backgroundColor={backgroundColor}
        />
      </React.Fragment>
    );
  }
}

export default App;
